// src/design/registry/colors.ts
// Single source of truth for mapping semantic "color tokens" to the active MUI
// theme palette. Components resolve tokens against the theme so the same registry
// works in both light and dark mode (no hardcoded hex).
import type { Theme } from '@mui/material/styles';

export type ColorToken =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral';

/** Resolve a semantic color token to a concrete color from the active theme. */
export const resolveColor = (token: ColorToken, theme: Theme): string =>
  token === 'neutral' ? theme.palette.text.secondary : theme.palette[token].main;

/** Soft tinted background for the same token (chips, badges, status dots). */
export const resolveSoftBg = (token: ColorToken, theme: Theme): string =>
  token === 'neutral' ? theme.palette.action.hover : `${theme.palette[token].main}20`;

/** Visual descriptor shared by every registry entry. */
export interface VisualMeta {
  /** MUI icon component (never an emoji string). */
  Icon: import('@mui/icons-material').SvgIconComponent;
  /** Semantic color token, resolved against the theme at render time. */
  color: ColorToken;
  /** i18n key for the user-facing label. */
  labelKey: string;
}
